import React, { useEffect, useRef, useState } from 'react';
import { ClarificationQuestion } from '../types';
import './ClarificationPanel.css';

interface ClarificationPanelProps {
  questions: ClarificationQuestion[];
  // One answer string per question, in question order — picked options
  // (plus the "Other" text, if any) comma-joined.
  onSubmit: (answers: string[]) => void;
}

interface AnswerDraft {
  picked: string[];
  otherChecked: boolean;
  otherText: string;
}

const emptyDraft = (): AnswerDraft => ({ picked: [], otherChecked: false, otherText: '' });

// The answer string sent for one question. Picked options keep the order the
// backend listed them in, with the "Other" text last.
const toAnswer = (q: ClarificationQuestion, d: AnswerDraft): string => {
  const parts = q.options.filter((o) => d.picked.includes(o));
  if (d.otherChecked && d.otherText.trim()) parts.push(d.otherText.trim());
  return parts.join(', ');
};

// Every question is required: something must be picked, and a ticked
// "Other" must have text.
const isAnswered = (d: AnswerDraft): boolean =>
  (d.picked.length > 0 || d.otherChecked) && (!d.otherChecked || d.otherText.trim() !== '');

// Replaces the chat input while the agent is waiting on a `clarification`
// message. Walks through the questions one at a time; each is multi-select
// over the backend's options plus a trailing free-text "Other".
const ClarificationPanel: React.FC<ClarificationPanelProps> = ({ questions, onSubmit }) => {
  const [step, setStep] = useState(0);
  const [drafts, setDrafts] = useState<AnswerDraft[]>(() => questions.map(emptyDraft));
  const otherInputRef = useRef<HTMLInputElement | null>(null);

  // A new clarification message starts over from the first question.
  useEffect(() => {
    setStep(0);
    setDrafts(questions.map(emptyDraft));
  }, [questions]);

  const question = questions[step];
  const draft = drafts[step] ?? emptyDraft();
  const isLast = step === questions.length - 1;
  const canContinue = isAnswered(draft);

  useEffect(() => {
    if (draft.otherChecked) otherInputRef.current?.focus();
  }, [draft.otherChecked, step]);

  const updateDraft = (patch: Partial<AnswerDraft>) => {
    setDrafts((prev) => prev.map((d, i) => (i === step ? { ...d, ...patch } : d)));
  };

  const toggleOption = (option: string) => {
    updateDraft({
      picked: draft.picked.includes(option)
        ? draft.picked.filter((o) => o !== option)
        : [...draft.picked, option],
    });
  };

  const goNext = () => {
    if (!canContinue) return;
    if (isLast) {
      onSubmit(questions.map((q, i) => toAnswer(q, drafts[i])));
    } else {
      setStep((s) => s + 1);
    }
  };

  if (!question) return null;

  return (
    <div className="clarification-panel" role="group" aria-labelledby="clarification-question">
      <div className="clarification-panel__header">
        <span className="clarification-panel__label">
          <i className="fas fa-question-circle" aria-hidden="true" /> A few details first
        </span>
        {questions.length > 1 && (
          <span className="clarification-panel__progress">
            {step + 1} of {questions.length}
          </span>
        )}
      </div>

      <p id="clarification-question" className="clarification-panel__question">
        {question.question}
      </p>

      <div className="clarification-panel__options">
        {question.options.map((option) => {
          const checked = draft.picked.includes(option);
          return (
            <label
              key={option}
              className={`clarification-panel__option${checked ? ' is-checked' : ''}`}
            >
              <input type="checkbox" checked={checked} onChange={() => toggleOption(option)} />
              <span>{option}</span>
            </label>
          );
        })}

        <label className={`clarification-panel__option${draft.otherChecked ? ' is-checked' : ''}`}>
          <input
            type="checkbox"
            checked={draft.otherChecked}
            onChange={() => updateDraft({ otherChecked: !draft.otherChecked })}
          />
          <span>Other</span>
        </label>
        {draft.otherChecked && (
          <input
            ref={otherInputRef}
            type="text"
            className="clarification-panel__other-input"
            placeholder="Type your answer"
            value={draft.otherText}
            onChange={(e) => updateDraft({ otherText: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                goNext();
              }
            }}
          />
        )}
      </div>

      <div className="clarification-panel__footer">
        {step > 0 && (
          <button
            type="button"
            className="clarification-panel__btn clarification-panel__btn--secondary"
            onClick={() => setStep((s) => s - 1)}
          >
            Back
          </button>
        )}
        <button
          type="button"
          className="clarification-panel__btn clarification-panel__btn--primary"
          onClick={goNext}
          disabled={!canContinue}
        >
          {isLast ? 'Submit' : 'Next'}
        </button>
      </div>
    </div>
  );
};

export default ClarificationPanel;
